/** @jsxImportSource @emotion/react */
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useParams } from "react-router-dom";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { Button } from "../../../Helper/Button";
import StartCheckFromOverviewModal from "../../Layers/StartCheckFromOverviewModal";
import useRipeningRoom from "../hooks/useRipeningRoom";

/**
 * Renders one bulk "Start <checkType> check (N selected)" button per check location
 * that has `start_on_ripening_room` enabled.
 *
 * Opens StartCheckFromOverviewModal (in selection mode) so the user can pick a
 * sample pallet and run the check on all selected pallets — the same UX as the
 * overview-tab bulk check flow.
 */
export default function RipeningRoomBulkCheckButtons() {
    const config = useConfig();
    const { selectedLayersArray, pallets, refreshAllocatedLayers, setSelectedLayers, activeRipeningCycle } = useRipeningRoom();
    const { location_id } = useParams<{ location_id: string }>();

    const [modalCheckType, setModalCheckType] = useState<string | null>(null);

    const selectedLayerObjects = pallets.filter((l: any) => selectedLayersArray.includes(l.id));
    const firstSelected = selectedLayerObjects[0];
    const hasSelection = selectedLayerObjects.length > 0;

    // * Build a stub for config eligibility lookup (fruit_type from first selected pallet, if available)
    const layerStub = hasSelection
        ? firstSelected
        : { type: "pallet", fruit_type: null };

    const eligibleLocations = config.get_check_types_config(layerStub);
    const visibleLocations = eligibleLocations.filter((l: any) => l.start_on_ripening_room);

    const handleClose = () => setModalCheckType(null);

    const handleSuccess = () => {
        setSelectedLayers({});
    };

    if (visibleLocations.length === 0) {
        return null;
    }

    return (
        <>
            {visibleLocations.map((loc: any) => {
                const checkTypeValue = loc.value;
                const label = loc?.text ?? checkTypeValue;
                const n = selectedLayerObjects.length;
                const requiresActiveCycle = !!loc.requires_active_ripening_cycle;
                const isDisabled = !hasSelection || (requiresActiveCycle && !activeRipeningCycle);
                return (
                    <Button
                        key={checkTypeValue}
                        color={n > 0 ? "primary" : "secondary"}
                        disabled={isDisabled}
                        icon={faAngleRight}
                        isInBottomBar
                        onClick={() => setModalCheckType(checkTypeValue)}
                    >
                        {label} check{n > 0 ? ` ${n}` : ""}
                    </Button>
                );
            })}
            {modalCheckType && firstSelected && (
                <StartCheckFromOverviewModal
                    isOpen={Boolean(modalCheckType)}
                    onClose={handleClose}
                    layer={firstSelected}
                    checkLocationValue={modalCheckType}
                    groupItems={selectedLayerObjects}
                    selectionMode
                    onSuccess={handleSuccess}
                    onRefresh={refreshAllocatedLayers}
                    extraCheckPayload={location_id ? { location_id } : undefined}
                />
            )}
        </>
    );
}
